import { useCallback, useMemo } from 'react'
import { useReactFlow } from '@xyflow/react'
import { useShallow } from 'zustand/shallow'
import { useAddNodeOnEdgeDropStore } from '@/stores/add-node-on-edge-drop-state'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { AVAILABLE_NODES, NODES_METADATA } from '../blocks'
import { BuilderNode, BuilderNodeType } from '../blocks/types'
import NodeList from './components/node-list'

type AddNodeFloatingMenuProps = Readonly<{
  onNodeAdd: (type: BuilderNodeType) => void
}>

export default function AddNodeFloatingMenu({
  onNodeAdd,
}: AddNodeFloatingMenuProps) {
  const { getNodes } = useReactFlow()
  const [showMenu, setShowMenu, anchorPosition, _incomingNodeMetadetails] =
    useAddNodeOnEdgeDropStore(
      useShallow((s) => [
        s.showMenu,
        s.actions.setShowMenu,
        s.anchorPosition,
        s.incomingNodeMetadetails,
      ])
    )

  const onOutsideClick = useCallback(() => {
    setShowMenu(false)
  }, [setShowMenu])

  const handleOnNodeAdd = useCallback(
    (type: BuilderNodeType) => {
      setShowMenu(false)
      onNodeAdd(type)
    },
    [onNodeAdd, setShowMenu]
  )

  const incomingNodeMetadetails = useMemo(
    () => _incomingNodeMetadetails,
    [_incomingNodeMetadetails]
  )

  const nodesWithMetadata = useMemo(() => {
    const existing = getNodes()
    const mapNodesWithMetadata = (
      types: BuilderNodeType[]
    ): Array<
      (typeof AVAILABLE_NODES)[number] & {
        __meta: (typeof NODES_METADATA)[BuilderNodeType]
        __enabled: boolean
      }
    > =>
      types.map((type) => {
        const meta = NODES_METADATA[type]
        const isFromSource =
          incomingNodeMetadetails?.fromHandle?.type === 'source'
        const isFromTarget =
          incomingNodeMetadetails?.fromHandle?.type === 'target'

        const enabledBase =
          (isFromSource && meta.connection.inputs > 0) ||
          (isFromTarget && meta.connection.outputs > 0) ||
          !incomingNodeMetadetails

        const isSingleton =
          type === BuilderNode.START || type === BuilderNode.END
        const alreadyExists = existing.some((n) => n.type === type)
        const enabled = enabledBase && (!isSingleton || !alreadyExists)

        return {
          type,
          title: meta.detail.title,
          icon: meta.detail.icon,
          description: meta.detail.description,
          gradientColor: meta.detail.gradientColor,
          __meta: meta,
          __enabled: enabled,
        }
      })

    return {
      availableNodes: mapNodesWithMetadata(
        AVAILABLE_NODES.map((node) => node.type)
      ),
      additionalNodes: mapNodesWithMetadata([BuilderNode.END]),
    }
  }, [incomingNodeMetadetails, getNodes])

  return (
    <Popover open={showMenu}>
      <PopoverTrigger asChild>
        <div
          className='absolute left size-0 bg-card'
          style={{
            left: `${anchorPosition.x}px`,
            top: `${anchorPosition.y}px`,
          }}
        />
      </PopoverTrigger>
      <PopoverContent
        className='animate-duration-200 isolate z-10 w-fit origin-[var(--radix-popover-content-transform-origin)] transform select-none overflow-clip rounded-xl border border-card-foreground/10 bg-card/40 p-0 shadow-2xl shadow-card/30 backdrop-blur-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out'
        side='bottom'
        align='start'
        tabIndex={-1}
        updatePositionStrategy='always'
        onFocusOutside={() => onOutsideClick()}
        onInteractOutside={() => onOutsideClick()}
        onEscapeKeyDown={() => onOutsideClick()}
      >
        <div tabIndex={-1} className='flex flex-col outline-none w-60'>
          <NodeList
            nodes={nodesWithMetadata.availableNodes}
            onNodeAdd={handleOnNodeAdd}
          />

          <div className='my-0.5 h-px bg-card-foreground/10' />

          <NodeList
            nodes={nodesWithMetadata.additionalNodes}
            onNodeAdd={handleOnNodeAdd}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
